"use client"
import {
  backButton,
  init,
  initData,
  miniApp,
  themeParams,
  viewport,
  initDataRaw,
  postEvent,
  mainButton,
  secondaryButton,
  settingsButton,
  $debug,
} from "@telegram-apps/sdk-react"

import {
  useState,
  ReactNode,
  useEffect,
  createContext,
  useContext,
  useMemo,
  Dispatch,
  SetStateAction,
} from "react"

import Axios from "axios"
import { captureException } from "@sentry/nextjs"
import { TgWagmiMonkeyProvider } from "./TgWagmiMonkeyProvider"
import {
  useAccount,
  useChainId,
  useReadContract,
  useWatchBlockNumber,
} from "wagmi"
import { useVaults, useVaultsQuery } from "../components/useVault"
import { formatAddress } from "../../lib"
import { TgGate } from "./TgGate"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { IS_PROD } from "../../IS_PROD"
import { blessnet, blessnetSepolia } from "../blessnet-chain-configs"
import { useTelegramMock } from "./useTelegramMock"
import { ABIBlessedAccount } from "./ABIBlessedAccount"
import { formatEther } from "viem"
import { UseQueryReturnType } from "wagmi/query"
import { MIN_ETH_TO_PROCESS_AA_OPERATION } from "."

const setup = () => {
  console.log("setup")
  $debug.set(true)
  init()
  // Mount all components used in the project.
  if (backButton.isSupported()) {
    backButton.mount()
  }
  miniApp.mount()
  themeParams.mount()
  initData.restore()
  mainButton.mount()
  mainButton.setParams({
    textColor: "#FFFFFF",
    backgroundColor: "#3F49CC",
    isVisible: false,
  })
  secondaryButton.mount()
  settingsButton.mount()
  // settingsButton.show()
  settingsButton.onClick(() => {
    // TODO: open blessnet bot?
    // const host = window.location.host
    // window.open
  })
  postEvent("web_app_set_header_color", { color: "#3F49CC" })

  Axios.defaults.headers["authorization"] = `tma ${initDataRaw()}`
  void viewport.mount().catch((e) => {
    console.error("Something went wrong mounting the viewport", e)
    captureException(e)
  })
}

type TgContextType = {
  balance: ReturnType<typeof useQuery<bigint>>
  account: ReturnType<
    typeof useQuery<{
      account: `0x${string}`
      exists: boolean
      hashedId: string
    }>
  >
  isModalOpen: boolean
  toggleModal: () => void
  setIsModalOpen: Dispatch<SetStateAction<boolean>>
  isLoading: boolean
  isFetched: boolean
  isSetup: boolean
  refetch: () => void
}
const TgContext = createContext<TgContextType>({} as TgContextType)
export const useTgContext = () => useContext(TgContext)

export const useTgAccount = () => {
  const chainId = useChainId()
  return useQuery<{
    account: `0x${string}`
    exists: boolean
    hashedId: string
  }>({
    queryKey: ["tg-account", chainId],
    queryFn: async () =>
      (
        await axios.get(
          `/api/tg/account?chain=${chainId === blessnet.id ? "mainnet" : "sepolia"}`,
        )
      ).data,
  })
}

// TODO: handle back button, etc
// https://github.com/Telegram-Mini-Apps/reactjs-js-tsdk-template/blob/master/src/components/App.jsx
export const TgRoot = ({
  children,
  loading,
}: {
  children: ReactNode
  loading?: ReactNode
}) => {
  const [isReady, setIsReady] = useState(false)
  if (process.env.NODE_ENV === "development") {
    // this won't run in a genuine tg app
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTelegramMock()
  }
  // const lp = useLaunchParams()
  useEffect(() => {
    setup()
    setIsReady(true)
  }, [setIsReady])

  const [isModalOpen, setIsModalOpen] = useState(false)
  const tgQuery = useTgAccount()

  const balance = useReadContract<typeof ABIBlessedAccount, "getDeposit", []>({
    address: tgQuery.data?.account,
    functionName: "getDeposit",
    abi: ABIBlessedAccount,
    chainId: IS_PROD ? blessnet.id : blessnetSepolia.id,
    query: {
      enabled: Boolean(tgQuery.data?.account),
    },
  })

  const vaults = useVaultsQuery(tgQuery.data?.account, true)

  const contextValue = useMemo(
    () => ({
      isModalOpen,
      toggleModal: () => setIsModalOpen(!isModalOpen),
      setIsModalOpen,
      balance: balance as UseQueryReturnType<bigint>,
      account: tgQuery,
      vaults,
      isFetched: tgQuery.isFetched && balance.isFetched && vaults.isFetched,
      isLoading: tgQuery.isLoading || balance.isLoading || vaults.isLoading,
      isSetup: Boolean(
        // your entry point is funded
        balance.data &&
          (balance.data as any) > 0n &&
          // and you've delegated
          vaults.data?.asDelegate.length > 0,
      ),
      refetch: () => {
        tgQuery.refetch()
        balance.refetch()
        vaults.refetch()
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isModalOpen,
      vaults.data,
      vaults.status,
      tgQuery.data,
      tgQuery.status,
      balance.data,
      balance.status,
    ],
  )

  useWatchBlockNumber({
    chainId: IS_PROD ? blessnet.id : blessnetSepolia.id,
    onBlockNumber: () => {
      contextValue.refetch()
    },
    enabled: !contextValue.isSetup,
  })

  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        {loading || "Loading..."}
      </div>
    )
  }
  return (
    <TgContext.Provider value={contextValue}>
      <TgWagmiMonkeyProvider blessnetAccount={tgQuery}>
        {children}

        <TgContext.Consumer>
          {({ isSetup }) => !isSetup && <TgGate />}
        </TgContext.Consumer>
        <Debug />
      </TgWagmiMonkeyProvider>
    </TgContext.Provider>
  )
}

const Debug = () => {
  const { address } = useAccount()
  const { onBehalfOf } = useVaults()
  const chainId = useChainId()
  const { balance } = useTgContext()
  return (
    <div className="px-2 py-3 text-white sticky bottom-0 bg-brand/80 backdrop-blur-sm text-xs hidden">
      connected as: {address ? formatAddress(address) : "noone"}
      <br />
      on behalf of: {onBehalfOf ? formatAddress(onBehalfOf) : "noone"}
      <br />
      chainId: {chainId}
      <br />
      balance:{" "}
      {balance.data
        ? `${String(balance.data)} ${formatEther(balance.data)}`
        : "no balance"}
      <br />
      can transact:{" "}
      {balance.data && balance.data > MIN_ETH_TO_PROCESS_AA_OPERATION}
    </div>
  )
}
